import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, useAuth } from "@/App";
import { toast } from "sonner";
import { 
  Loader2,
  FileSignature,
  Check,
  X,
  ArrowLeft,
  Car,
  Euro,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function SignaturePage() {
  const { user } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  const [objective, setObjective] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signing, setSigning] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    fetchObjective();
  }, [id]);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "white";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#212E38";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [loading]);

  const fetchObjective = async () => {
    try {
      const res = await api.get(`/objectives/${id}`);
      setObjective(res.data);
      
      // Check if user can sign
      const obj = res.data;
      if (user?.role === "seller" && obj.status !== "pending_seller") {
        toast.error("Cet objectif n'est pas en attente de votre signature");
        navigate("/seller");
        return;
      }
      if (["admin", "manager"].includes(user?.role) && obj.status !== "pending_manager") {
        toast.error("Cet objectif n'est pas en attente de signature responsable");
        navigate("/objectives");
        return;
      }
    } catch (error) {
      toast.error("Objectif non trouvé");
      navigate("/");
    } finally {
      setLoading(false);
    }
  };

  const startDrawing = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    setHasDrawn(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const handleSign = async () => {
    if (!signatureName.trim() && !hasDrawn) {
      toast.error("Veuillez saisir votre nom ou dessiner votre signature");
      return;
    }

    setSigning(true);
    try {
      const signatureData = hasDrawn 
        ? canvasRef.current.toDataURL("image/png")
        : `Signé électroniquement par: ${signatureName}`;

      const signerType = user?.role === "seller" ? "seller" : "manager";
      
      await api.post(`/objectives/${id}/sign`, {
        signature_data: signatureData,
        signer_type: signerType
      });

      toast.success("Objectifs signés avec succès !");
      
      if (user?.role === "seller") {
        navigate("/seller");
      } else {
        navigate("/objectives");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la signature");
    } finally {
      setSigning(false);
    }
  };

  const monthNames = ["", "Janvier", "Février", "Mars", "Avril", "Mai", "Juin", 
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

  if (loading) {
    return (
      <div className="signature-page flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#E30613]" />
      </div>
    );
  }

  if (!objective) {
    return null;
  }

  return (
    <div className="signature-page">
      {/* Back button */}
      <div className="max-w-3xl mx-auto mb-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-gray-600"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour
        </Button>
      </div>

      {/* Document */}
      <div className="signature-document animate-fade-in" data-testid="signature-document">
        {/* Header */}
        <div className="signature-header">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-12 bg-[#E30613] rounded-lg flex items-center justify-center">
              <Car className="w-7 h-7 text-white" />
            </div>
            <div>
              <h1>GROUPE VERBAERE</h1>
              <p className="text-sm text-gray-500">Feuille d'objectifs commerciaux</p>
            </div>
          </div>
          <h2 className="text-xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {monthNames[objective.month]} {objective.year}
          </h2>
        </div>

        {/* Info */}
        <div className="grid grid-cols-2 gap-6 mb-8 p-4 bg-gray-50 rounded-lg">
          <div>
            <p className="text-sm text-gray-500">Vendeur</p>
            <p className="font-bold text-lg">{objective.seller_name}</p>
          </div>
          <div>
            <p className="text-sm text-gray-500">Concession</p>
            <p className="font-bold text-lg">{objective.concession_name}</p>
          </div>
        </div>

        {/* Objectives Summary - Simplified */}
        <div className="space-y-6 mb-8">
          {/* VN */}
          <div className="border rounded-lg p-6">
            <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
              <Car className="w-5 h-5 text-[#E30613]" />
              Objectif Véhicules Neufs (VN)
            </h3>
            <div className="text-center p-4 bg-gray-50 rounded-lg max-w-xs">
              <p className="text-4xl font-bold text-[#E30613]">{objective.vn_target}</p>
              <p className="text-sm text-gray-500 mt-1">véhicules neufs</p>
            </div>
          </div>

          {/* VO */}
          <div className="border rounded-lg p-6">
            <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
              <Car className="w-5 h-5 text-[#E30613]" />
              Objectif Véhicules d'Occasion (VO)
            </h3>
            <div className="text-center p-4 bg-gray-50 rounded-lg max-w-xs">
              <p className="text-4xl font-bold text-[#E30613]">{objective.vo_target}</p>
              <p className="text-sm text-gray-500 mt-1">véhicules d'occasion</p>
            </div>
          </div>

          {/* Financement */}
          <div className="border rounded-lg p-6">
            <h3 className="font-bold text-gray-700 mb-4 flex items-center gap-2">
              <Euro className="w-5 h-5 text-[#E30613]" />
              Objectif Financement
            </h3>
            <div className="text-center p-4 bg-gray-50 rounded-lg max-w-xs">
              <p className="text-4xl font-bold text-[#E30613]">{objective.financing_target?.toLocaleString()} €</p>
              <p className="text-sm text-gray-500 mt-1">chiffre d'affaires financement</p>
            </div>
          </div>
        </div>

        {/* Notes */}
        {objective.notes && (
          <div className="mb-8 p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">Notes</p>
            <p className="text-gray-700">{objective.notes}</p>
          </div>
        )}

        {/* Previous signatures */}
        {objective.seller_signature && (
          <div className="mb-6 p-4 border rounded-lg">
            <p className="text-sm text-gray-500 mb-2">Signature du vendeur</p>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Check className="w-5 h-5 text-green-500" />
                <span className="font-medium">Signé le {new Date(objective.seller_signed_at).toLocaleDateString('fr-FR')}</span>
              </div>
              {objective.seller_signature.startsWith("data:image") ? (
                <img src={objective.seller_signature} alt="Signature" className="h-16" />
              ) : (
                <span className="text-gray-600 italic">{objective.seller_signature}</span>
              )}
            </div>
          </div>
        )}

        {/* Signature Section */}
        <div className="border-t-2 border-[#E30613] pt-6">
          <h3 
            className="text-lg font-bold text-[#212E38] mb-4 flex items-center gap-2"
            style={{ fontFamily: 'Chivo, sans-serif' }}
          >
            <FileSignature className="w-5 h-5 text-[#E30613]" />
            {user?.role === "seller" ? "Votre signature" : "Signature du responsable"}
          </h3>

          {/* Signature by name */}
          <div className="mb-6">
            <Label htmlFor="signatureName">Signer en tapant votre nom</Label>
            <Input
              id="signatureName"
              value={signatureName}
              onChange={(e) => setSignatureName(e.target.value)}
              placeholder="Votre nom complet"
              className="max-w-md mt-2"
              data-testid="signature-name-input"
            />
          </div>

          {/* Signature by drawing */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-2">
              <Label>Ou dessiner votre signature</Label>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={clearCanvas}
                className="text-gray-500"
                data-testid="clear-signature-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Effacer
              </Button>
            </div>
            <canvas
              ref={canvasRef}
              width={400}
              height={150}
              className="signature-canvas w-full max-w-md cursor-crosshair"
              onMouseDown={startDrawing}
              onMouseMove={draw}
              onMouseUp={stopDrawing}
              onMouseLeave={stopDrawing}
              onTouchStart={startDrawing}
              onTouchMove={draw}
              onTouchEnd={stopDrawing}
              data-testid="signature-canvas"
            />
          </div>

          {/* Legal text */}
          <p className="text-sm text-gray-500 mb-6">
            En signant ce document, je reconnais avoir pris connaissance des objectifs 
            qui me sont fixés pour la période {monthNames[objective.month]} {objective.year} 
            et m'engage à les atteindre.
          </p>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex-1"
              data-testid="cancel-sign-btn"
            >
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
            <Button
              onClick={handleSign}
              className="btn-primary flex-1"
              disabled={signing || (!signatureName.trim() && !hasDrawn)}
              data-testid="confirm-sign-btn"
            >
              {signing ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Signature en cours...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Signer les objectifs
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
